import { injectable } from 'inversify';
import {
  isSameDay,
  isSameMonth,
  startOfDay,
  endOfDay,
  subDays,
  isWeekend,
  getYear,
  eachDayOfInterval,
  formatISO,
  add,
  sub,
  max,
  closestTo,
  differenceInCalendarDays,
  differenceInBusinessDays,
  differenceInDays,
  endOfMonth,
  endOfYear,
  isValid,
  addDays,
  isWithinInterval,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
} from 'date-fns';

import { UnmarshallerService } from '../unmarshaller';
import { HttpService } from '../http';
import { FormatService } from '../format';

import { WorkCalendar, WorkScheduleCalendar } from './gen/calendar';
import { ThresholdDateOptions } from './types';

const DECEMBER = 11;
const NEW_YEAR_ICON_START_DAY = 10;
const NEW_YEAR_ICON_END_DAY = 14;

@injectable()
export class Calendar {
  static minDate = new Date('1900-01-01');

  static maxDate = new Date('2100-01-01');

  private holidaysAndWeekends: Record<number, Record<string, boolean>> = {};

  private holidays: Record<number, Record<string, boolean>> = {};

  private downloading: Record<number, boolean> = {};

  private workScheduleResults: Record<number, WorkScheduleCalendar> = {};

  constructor(
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private format: FormatService,
  ) {}

  /**
   * Вычисляет пороговую дату: from_date (с начала дня) плюс days_count
   * рабочих, календарных или не учитывающих праздники дней
   *
   * @param options - Параметры расчета
   * @returns Пороговая дата
   */
  private getThresholdDate(options: ThresholdDateOptions): Date {
    const startDate = this.startOfDay(options.from_date);

    if (options.working_days_only) {
      return this.addBusinessDays(startDate, options.days_count);
    }

    if (options.ignore_holidays) {
      return this.addWithoutHolidayDays(startDate, options.days_count);
    }

    return add(startDate, { days: options.days_count });
  }

  /**
   * Загрузить календарь выходных и праздничных дней
   *
   * @param date - Дата, по которой будет вычисляться год для загружаемого календаря
   * @param id - Id сотрудника необходим, если загружается календарь для конкретного сотрудника
   */
  async download(date = new Date(), id?: string) {
    const year = getYear(date);
    const defaultYear = 2021;

    if (year < defaultYear) return;

    try {
      if (!this.holidaysAndWeekends[year] && !this.downloading[year]) {
        this.downloading[year] = true;
        const result = await this.unmarshaller.unmarshall(
          await this.http.get(
            `/work_calendar/${year}`,
            id ? { employee_id: id } : {},
            { withSide: false },
          ),
          WorkCalendar,
        );

        this.holidaysAndWeekends[year] = result.dates
          .filter(({ type }) => type === 'holiday' || type === 'weekend')
          .reduce((acc, { date: day }) => ({ ...acc, [day]: true }), {});
        this.holidays[year] = result.dates
          .filter(({ type }) => type === 'holiday')
          .reduce((acc, { date: day }) => ({ ...acc, [day]: true }), {});
        this.downloading[year] = false;
      }
    } catch (e) {
      // proceed anyway
      this.downloading[year] = false;
    }
  }

  /**
   * Загрузить график работы сотрудника на год
   * @param year - Год для загрузки графика
   * @param employeeId - ID сотрудника
   * @returns График работы или null при ошибке
   */
  async downloadWorkSchedule(
    year: number,
    employeeId: string,
  ): Promise<WorkScheduleCalendar | null> {
    const defaultYear = 2021;

    if (year < defaultYear) return null;

    if (this.holidaysAndWeekends[year] || this.downloading[year]) {
      return this.workScheduleResults[year] || null;
    }

    try {
      this.downloading[year] = true;

      const result = await this.unmarshaller.unmarshall(
        await this.http.get(
          `/work_schedule/${year}`,
          { employee_id: employeeId },
          { withSide: false },
        ),
        WorkScheduleCalendar,
      );

      this.workScheduleResults[year] = result;
      this.holidaysAndWeekends[year] = {};
      this.holidays[year] = {};

      result.dates.forEach(({ date, is_holiday, is_workday }) => {
        if (is_holiday) {
          this.holidays[year][date] = true;
        }

        if (!is_workday) {
          this.holidaysAndWeekends[year][date] = true;
        }
      });

      this.downloading[year] = false;

      return result;
    } catch (e) {
      this.downloading[year] = false;
      return null;
    }
  }

  /**
   * Получить массив праздничных дней за выбранный год
   * на основе ранее загруженных календарей
   *
   * @param year - Год, для которого получаем массив праздничных дней
   * @returns Массив дат праздничных дней
   */
  getHolidays(year: number): Date[] {
    return Object.keys(this.holidays[year] || {}).map((day) =>
      this.startOfDay(new Date(day)),
    );
  }

  /**
   * Получить массив праздничных и выходных дней за выбранный год
   * на основе ранее загруженных календарей
   *
   * @param year - Год, для которого получаем массив праздничных и выходных дней
   * @returns Массив дат праздничных и выходных дней
   */
  getHolidaysOrWeekends(year: number): Date[] {
    return Object.keys(this.holidaysAndWeekends[year] || {}).map((day) =>
      this.startOfDay(new Date(day)),
    );
  }

  /**
   * Сбросить все данные по сохранненным ранее календарям
   */
  reset() {
    this.holidaysAndWeekends = {};
    this.holidays = {};
    this.downloading = {};
    this.workScheduleResults = {};
  }

  /**
   * Преобразовать дату в формат ISO
   *
   * @param date - Дата, которая должна быть преобразована
   * @param options - Параметры для преобразования к ISO
   * @returns Строка в формате ISO
   */
  formatISO(
    date: Date,
    options: { representation: 'complete' | 'date' | 'time' } = {
      representation: 'complete',
    },
  ) {
    return formatISO(date, options);
  }

  /**
   * Получить новую дату путем прибавления количества дней к выбранной дате
   *
   * @param date - Дата, на основе которой будет получена новая дата
   * @param amount - Количество дней
   * @returns Новая дата
   */
  add(date: Date, amount: number): Date {
    return add(date, { days: amount });
  }

  /**
   * Получить новую дату путем прибавления количества дней к выбранной дате
   * за вычетом праздничных дней
   *
   * @param date - Дата, на основе которой будет получена новая дата
   * @param amount - Количество дней
   * @returns Новая дата
   */
  addWithoutHolidayDays(date: Date, amount: number): Date {
    let count = 0;
    let result = date;

    while (count < amount) {
      result = add(result, { days: 1 });

      if (!this.isHoliday(result)) {
        count += 1;
      }
    }

    return result;
  }

  /**
   * Получить новую дату путем прибавления количества рабочих дней к выбранной дате
   *
   * @param date - Дата, на основе которой будет получена новая дата
   * @param amount - Количество рабочих дней
   * @returns Новая дата
   */
  addBusinessDays(date: Date, amount: number): Date {
    let count = 0;
    let result = date;

    while (count < amount) {
      result = add(result, { days: 1 });

      if (!this.isHolidayOrWeekend(result)) {
        count += 1;
      }
    }

    return result;
  }

  /**
   * Получить новую дату путем вычитания количества дней из выбранной даты
   *
   * @param date - Дата, на основе которой будет получена новая дата
   * @param amount - Количество дней
   * @returns Новая дата
   */
  sub(date: Date, amount: number): Date {
    return sub(date, { days: amount });
  }

  /**
   * Получить новую дату путем вычитания количества минут из выбранной даты
   *
   * @param date - Дата, на основе которой будет получена новая дата
   * @param amount - Количество минут
   * @returns Новая дата
   */
  subMinutes(date: Date, amount: number): Date {
    return sub(date, { minutes: amount });
  }

  /**
   * Получить новую дату путем вычитания количества рабочих дней из выбранной даты
   *
   * @param date - Дата, на основе которой будет получена новая дата
   * @param amount - Количество рабочих дней
   * @returns Новая дата
   */
  subBusinessDays(date: Date, amount: number): Date {
    let count = 0;
    let result = date;

    while (count < amount) {
      result = sub(result, { days: 1 });

      if (!this.isHolidayOrWeekend(result)) {
        count += 1;
      }
    }

    return result;
  }

  /**
   * Определить является выбранная дата выходным или праздничным днем
   * на основе ранее загруженных календарей
   *
   * @param date - Выбранная дата
   * @returns true, если дата является выходным или праздничным днем
   */
  isHolidayOrWeekend(date: Date): boolean {
    const year = getYear(date);

    if (this.holidaysAndWeekends[year]) {
      return !!this.holidaysAndWeekends[year][
        formatISO(date, { representation: 'date' })
      ];
    }

    return isWeekend(date);
  }

  /**
   * Определить является выбранная дата праздничным днем
   * на основе ранее загруженных календарей
   *
   * @param date - Выбранная дата
   * @returns true, если дата является праздничным днем
   */
  isHoliday(date: Date): boolean {
    const year = getYear(date);

    if (this.holidays[year]) {
      return !!this.holidays[year][formatISO(date, { representation: 'date' })];
    }

    return false;
  }

  /**
   * Определить совпадает год, месяц и день у двух выбранных дат
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @returns true, если даты совпадают по году, месяцу и дню
   */
  isSameDay(date1: Date, date2: Date): boolean {
    return isSameDay(date1, date2);
  }

  /**
   * Определить совпадает год и месяц у двух выбранных дат
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @returns true, если даты совпадают по году и месяцу
   */
  isSameMonth(date1: Date, date2: Date): boolean {
    return isSameMonth(date1, date2);
  }

  /**
   * Определить количество полных дней между двумя датами
   * Дробные дни округляются до нуля
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @param options - Опции расчета
   * @returns Количество дней
   */
  differenceInDays(
    date1: Date,
    date2: Date,
    options?: {
      excludeHolidays?: boolean;
      excludeWorkingDays?: boolean;
      workingDaysOnly?: boolean;
    },
  ): number {
    if (options?.excludeHolidays) {
      return (
        differenceInDays(date1, date2) -
        this.countOfHolidaysInInterval(date2, date1)
      );
    }

    if (options?.workingDaysOnly) {
      return (
        differenceInDays(date1, date2) -
        this.countOfHolidaysInInterval(date2, date1) -
        this.countOfWeekendsInInterval(date1, date2)
      );
    }

    if (options?.excludeWorkingDays) {
      return differenceInBusinessDays(date1, date2);
    }

    return differenceInDays(date1, date2);
  }

  /**
   * Получить дату начала выбранного дня
   * Результат будет в локальной временной зоне
   *
   * @param date - Выбранная дата
   * @returns Дата начала дня
   */
  startOfDay(date: Date): Date {
    return startOfDay(date);
  }

  /**
   * Получить дату конца выбранного дня
   * Результат будет в локальной временной зоне
   *
   * @param date - Выбранная дата
   * @returns Дата конца дня
   */
  endOfDay(date: Date): Date {
    return endOfDay(date);
  }

  hasAvailableDatesAfter(
    since: Date,
    intervals: { active_from: string; active_to?: string }[],
  ) {
    if (!intervals.length || intervals[0].active_to) {
      return true;
    }

    const start = startOfDay(since);
    let end = startOfDay(new Date(intervals[0].active_from));

    if (intervals.length === 1) {
      if (subDays(end, 1) <= start) {
        return false;
      } else {
        return true;
      }
    }

    for (let i = 0; i < intervals.length; i++) {
      const { active_from: activeFrom, active_to: activeTo } = intervals[i];

      const dateFrom = startOfDay(new Date(activeFrom));
      const dateTo = startOfDay(
        activeTo ? new Date(activeTo) : new Date('2100-01-01'),
      );

      if (isSameDay(dateTo, end) || isSameDay(dateTo, subDays(end, 1))) {
        end = dateFrom;
      }

      if (subDays(end, 1) <= start) {
        return false;
      }
    }

    return true;
  }

  /**
   * Вернуть дату из массива дат, ближайшую к выбранной дате
   * Если в массиве отсутствуют валидные даты, то возвращается undefined
   *
   * @param date - Выбранная дата
   * @param dates - Массив дат
   * @returns Ближайшая дата или undefined
   */
  closestTo(date: Date, dates: Date[]) {
    return closestTo(date, dates);
  }

  /**
   * Определить количество календарных дней между двумя датами
   * Часть даты, отвечающая за время, исключяется из рассмотрения
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @returns Количество календарных дней
   */
  differenceInCalendarDays(date1: Date, date2: Date) {
    return differenceInCalendarDays(date1, date2);
  }

  /**
   * Определить количество дней отпуска между двумя датами
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @returns Количество дней отпуска
   */
  countOfVacationDays(date1: Date, date2: Date) {
    return (
      differenceInCalendarDays(date1, date2) +
      1 -
      this.countOfHolidaysInInterval(date2, date1)
    );
  }

  /**
   * Входит ли дата в один из интервалов
   *
   * @param date - Выбранная дата
   * @param intervals - Массив интервалов
   * @returns true, если дата входит в любой из интервалов
   */
  isWithinIntervals(date: Date, intervals: [start: Date, end: Date][]) {
    return intervals.some((interval) =>
      isWithinInterval(date, { start: interval[0], end: interval[1] }),
    );
  }

  /**
   * Вернуть дату конца месяца на основе выбранной даты
   *
   * @param date - Выбранная дата
   * @returns Дата конца месяца
   */
  endOfMonth(date: Date) {
    return endOfMonth(date);
  }

  /**
   * Вернуть дату конца выбранного года
   *
   * @param year - Выбранный год
   * @returns Дата конца года (31 декабря)
   */
  endOfYear(year: number): Date {
    return endOfYear(new Date(year, 0, 1));
  }

  /**
   * Преобразовать строку с датой в объект Date
   * Если строка пустая/отсутствует, возвращается undefined
   *
   * @param dateString - Строка с датой
   * @returns Дата или undefined
   */
  parseDate(dateString?: string | null): Date | undefined {
    return dateString ? new Date(dateString) : undefined;
  }

  /**
   * Вернуть самую позднюю дату из переданных, приведённую к началу дня
   * undefined/null в массиве игнорируются
   *
   * @param dates - Массив дат (может содержать undefined/null)
   * @returns Самая поздняя дата (в начале дня) или undefined, если валидных дат нет
   */
  latestDate(dates: (Date | undefined | null)[]): Date | undefined {
    const validDates = dates
      .filter((date): date is Date => !!date)
      .map((date) => this.startOfDay(date));

    return validDates.length ? max(validDates) : undefined;
  }

  /**
   * Определить идет Дата №1 раньше Даты №2
   *
   * @param date - Дата №1
   * @param minDate - Дата №2
   * @returns true, если date раньше minDate
   */
  isLessThan(date: Date, minDate: Date) {
    return date < minDate;
  }

  /**
   * Определить идет Дата №1 позже Даты №2
   *
   * @param date - Дата №1
   * @param maxDate - Дата №2
   * @returns true, если date позже maxDate
   */
  isMoreThan(date: Date, maxDate: Date) {
    return date > maxDate;
  }

  /**
   * Определить одинаковы даты или Дата №1 идет позже Даты №2
   * с учетом UTC
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @returns true, если date1 \>= date2
   */
  isEqualOrMore(date1: Date, date2: Date): boolean {
    return date1.getTime() >= date2.getTime();
  }

  isTimeForNewYear() {
    const currentDay = new Date().getDate();
    const currentMonth = new Date().getMonth();

    if (currentMonth === 0 && currentDay <= NEW_YEAR_ICON_END_DAY) {
      return true;
    }

    if (currentMonth === DECEMBER && currentDay >= NEW_YEAR_ICON_START_DAY) {
      return true;
    }

    return false;
  }

  /**
   * Дата является валидной, если она наступает раньше даты, вычисляемой из опций
   *
   * @param date - Валидируемая дата
   * @param options - Параметры расчета
   * @returns true, если date раньше вычисленной даты
   */
  isNotWithinDays(date: Date, options: ThresholdDateOptions) {
    return date < this.getThresholdDate(options);
  }

  /**
   * Дата является валидной, если она наступает не раньше даты, вычисляемой из опций
   *
   * @param date - Валидируемая дата
   * @param options - Параметры расчета
   * @returns true, если date не раньше вычисленной даты
   */
  isWithinDays(date: Date, options: ThresholdDateOptions) {
    return this.startOfDay(date) >= this.getThresholdDate(options);
  }

  /**
   * Дата является валидной, если она попадает в любой интервал из переданных в options
   *
   * @param date - Валидируемая дата
   * @param options - Параметры с массивом интервалов
   * @returns true, если date входит в любой из интервалов
   */
  isInNotWithinIntervals(
    date: Date,
    options: {
      existing_dates: { from_date: string; to_date: string }[];
    },
  ) {
    return options.existing_dates.some((interval) => {
      return (
        date >= this.startOfDay(new Date(interval.from_date)) &&
        date <= new Date(interval.to_date)
      );
    });
  }

  /**
   * Возвращает интервал, содержащий валидируемую дату
   *
   * @param date - Валидируемая дата
   * @param options - Параметры с массивом интервалов
   * @returns Интервал, содержащий дату, или undefined
   */
  getOverlapInterval(
    date: Date,
    options: {
      existing_dates: {
        from_date: string;
        to_date: string;
        action: 'error' | 'warning';
        message: string;
      }[];
    },
  ) {
    return options.existing_dates.find((interval) => {
      return (
        date >= this.startOfDay(new Date(interval.from_date)) &&
        date <= new Date(interval.to_date)
      );
    });
  }

  /**
   * Дата является валидной, если среди переданных интервалов дат есть хотя бы один, чье начало не раньше from_date,
   * но раньше валидируемой даты
   *
   * @param date - Валидируемая дата
   * @param options - Параметры с from_date и массивом интервалов
   * @returns true, если date попадает в интервал после from_date
   */
  isInNotWithinIntervalsAfterDate(
    date: Date,
    options: {
      from_date: Date;
      existing_dates: { from_date: string; to_date: string }[];
    },
  ) {
    if (this.startOfDay(options.from_date) > date) {
      return true;
    }

    const startDays = [...options.existing_dates]
      .sort((first, second) =>
        new Date(first.from_date) > new Date(second.from_date) ? 1 : -1,
      )
      .map(({ from_date }) => from_date);

    const maxDate = startDays.find(
      (day) =>
        this.startOfDay(new Date(day)) >= this.startOfDay(options.from_date),
    );

    if (!maxDate) {
      return false;
    }

    return date >= this.startOfDay(new Date(maxDate));
  }

  /**
   * Определить все дни из интервала являются праздничными/выходными
   *
   * @param date - Выбранная дата
   * @param options - Параметры интервала
   * @returns true, если все дни в интервале праздничные/выходные
   */
  areAllHolidaysInInterval(
    date: Date,
    options: {
      from_date: Date;
      exclude_weekends?: boolean;
    },
  ) {
    const fromDate = this.startOfDay(options.from_date);

    if (this.startOfDay(options.from_date) > date) {
      return false;
    }

    return eachDayOfInterval({
      start: fromDate,
      end: date,
    }).every((day) =>
      options.exclude_weekends
        ? this.isHoliday(day)
        : this.isHolidayOrWeekend(day),
    );
  }

  /**
   * Определить количество праздничных дней между двумя датами
   * на основе ранее загруженных календарей
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @returns Количество праздничных дней
   */
  countOfHolidaysInInterval(date1: Date, date2: Date) {
    return eachDayOfInterval({
      start: date1 < date2 ? date1 : date2,
      end: date1 < date2 ? date2 : date1,
    }).filter((day) => this.isHoliday(day)).length;
  }

  /**
   * Определить количество выходных дней между двумя датами
   * на основе ранее загруженных календарей
   *
   * @param date1 - Дата №1
   * @param date2 - Дата №2
   * @returns Количество выходных дней
   */
  countOfWeekendsInInterval(date1: Date, date2: Date) {
    return (
      eachDayOfInterval({
        start: date1 < date2 ? date1 : date2,
        end: date1 < date2 ? date2 : date1,
      }).filter((day) => this.isHolidayOrWeekend(day)).length -
      this.countOfHolidaysInInterval(date2, date1)
    );
  }

  /**
   * Определить является выбранная дата валидной
   *
   * @param date - Выбранная дата
   * @returns true, если дата валидна
   */
  isValidDate(date: string | null | Date) {
    return isValid(date);
  }

  getPreviousDayIsWeekendOrHoliday(date: Date, previousDayCount: number) {
    const yesterday = subDays(date, previousDayCount);
    return this.isHolidayOrWeekend(yesterday);
  }

  getNextDayIsWeekendOrHoliday(date: Date, afterDaycount: number) {
    const afternoon = addDays(date, afterDaycount);
    return this.isHolidayOrWeekend(afternoon);
  }

  /**
   * Получить все дни между двумя датами
   *
   * @param start - Дата начала интервала
   * @param end - Дата конца интервала
   * @returns Массив дат
   */
  getAllDaysOfInterval(start: Date, end: Date) {
    return eachDayOfInterval({ start, end });
  }

  /**
   * Получить все интервалы выходных или праздничных дней
   *
   * @param range - Диапазон дат
   * @returns Строка с интервалами
   */
  getHolidaysOrWeekendsIntervals(range: Date[]) {
    const intervals: Date[][] = [];

    range.forEach((day, index) => {
      if (this.isHolidayOrWeekend(day)) {
        const intervalsLength = intervals.length;
        if (range[index - 1] && this.isHolidayOrWeekend(range[index - 1])) {
          intervals[intervalsLength - 1] = [
            intervals[intervalsLength - 1][0],
            day,
          ];
        } else {
          intervals[intervalsLength] = [day];
        }
      }
    });

    return intervals
      .map((interval) =>
        interval
          .map((date) => this.format.toDate(date.toDateString()))
          .join(' - '),
      )
      .join(', ');
  }

  /**
   * Дата является валидной, если она является сегодняшней
   *
   * @param date - Выбранная дата
   * @returns true, если дата сегодняшняя
   */
  isToday(date: Date | string) {
    return isToday(new Date(date));
  }

  /**
   * Дата является валидной, если она является вчерашней
   *
   * @param date - Выбранная дата
   * @returns true, если дата вчерашняя
   */
  isYesterday(date: Date | string) {
    return isYesterday(new Date(date));
  }

  /**
   * Дата является валидной, если она входит в текущую неделю
   *
   * @param date - Выбранная дата
   * @param weekStartsOn - День недели, с которого начинается неделя
   * @returns true, если дата в текущей неделе
   */
  // eslint-disable-next-line no-magic-numbers
  isThisWeek(date: Date | string, weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
    return isThisWeek(new Date(date), { weekStartsOn });
  }

  /**
   * Дата является валидной, если она входит в текущий месяц
   *
   * @param date - Выбранная дата
   * @returns true, если дата в текущем месяце
   */
  isThisMonth(date: Date | string) {
    return isThisMonth(new Date(date));
  }
}
